import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/copytrade.css";

const API_BASE_URL = "https://primerise.space";

const CopyTrade = () => {
  const navigate = useNavigate();

  const [liveSlot, setLiveSlot] = useState(null);
  const [myActiveTrade, setMyActiveTrade] = useState(null);
  const [history, setHistory] = useState([]);

  const [loadingLiveSlot, setLoadingLiveSlot] = useState(true);
  const [loadingMyTrade, setLoadingMyTrade] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [joiningTrade, setJoiningTrade] = useState(false);

  const [countdown, setCountdown] = useState("00:00");
  const [pakistanNow, setPakistanNow] = useState(new Date());

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("primeriseToken") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const getAuthHeaders = (includeJson = false) => {
    const headers = {};
    const token = getToken();

    if (includeJson) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined || value === "") return "0.00";
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return value;

    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getPakistanParts = (dateValue) => {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Karachi",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    const parts = formatter.formatToParts(new Date(dateValue));
    const map = {};

    parts.forEach((part) => {
      if (part.type !== "literal") {
        map[part.type] = part.value;
      }
    });

    return {
      year: map.year,
      month: map.month,
      day: map.day,
      hour: Number(map.hour || 0),
      minute: Number(map.minute || 0),
      dateKey: `${map.year}-${map.month}-${map.day}`,
    };
  };

  const formatPakistanDateTime = (dateValue) => {
    if (!dateValue) return "Completed";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) return "Completed";

    return date.toLocaleString("en-US", {
      timeZone: "Asia/Karachi",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatHistoryDayLabel = (dateValue) => {
    if (!dateValue) return "Unknown Date";

    const currentParts = getPakistanParts(new Date());
    const targetParts = getPakistanParts(dateValue);

    const currentDate = new Date(
      `${currentParts.year}-${currentParts.month}-${currentParts.day}T00:00:00`
    );
    const targetDate = new Date(
      `${targetParts.year}-${targetParts.month}-${targetParts.day}T00:00:00`
    );

    const diffDays = Math.round(
      (currentDate.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";

    return new Date(dateValue).toLocaleDateString("en-US", {
      timeZone: "Asia/Karachi",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const fetchLiveSlot = async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoadingLiveSlot(true);
      }

      const response = await fetch(`${API_BASE_URL}/api/copy-trades/live`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setLiveSlot(null);
        return;
      }

      setLiveSlot(data.trade || null);
    } catch (error) {
      console.error("Live slot fetch error:", error);
      setLiveSlot(null);
    } finally {
      if (!isBackground) {
        setLoadingLiveSlot(false);
      }
    }
  };

  const fetchMyActiveTrade = async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoadingMyTrade(true);
      }

      const response = await fetch(`${API_BASE_URL}/api/copy-trades/my-active`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setMyActiveTrade(null);
        return;
      }

      setMyActiveTrade(data.trade || null);
    } catch (error) {
      console.error("My active trade fetch error:", error);
      setMyActiveTrade(null);
    } finally {
      if (!isBackground) {
        setLoadingMyTrade(false);
      }
    }
  };

  const fetchTradeHistory = async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoadingHistory(true);
      }

      const response = await fetch(`${API_BASE_URL}/api/copy-trades/history`, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        setHistory([]);
        return;
      }

      setHistory(Array.isArray(data.history) ? data.history : []);
    } catch (error) {
      console.error("Trade history fetch error:", error);
      setHistory([]);
    } finally {
      if (!isBackground) {
        setLoadingHistory(false);
      }
    }
  };

  const refreshAll = async (isBackground = false) => {
    await Promise.all([
      fetchLiveSlot(isBackground),
      fetchMyActiveTrade(isBackground),
      fetchTradeHistory(isBackground),
    ]);
  };

  const refreshLiveData = async (isBackground = false) => {
    await Promise.all([
      fetchLiveSlot(isBackground),
      fetchMyActiveTrade(isBackground),
    ]);
  };

  useEffect(() => {
    refreshAll(false);

    const liveInterval = setInterval(() => {
      refreshLiveData(true);
    }, 30000);

    const historyInterval = setInterval(() => {
      fetchTradeHistory(true);
    }, 120000);

    return () => {
      clearInterval(liveInterval);
      clearInterval(historyInterval);
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setPakistanNow(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!myActiveTrade?.settlementAt) {
      setCountdown("00:00");
      return;
    }

    const updateCountdown = () => {
      const now = new Date().getTime();
      const settlementAt = new Date(myActiveTrade.settlementAt).getTime();

      if (Number.isNaN(settlementAt)) {
        setCountdown("00:00");
        return;
      }

      const remaining = settlementAt - now;

      if (remaining <= 0) {
        setCountdown("00:00");
        return;
      }

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);

      setCountdown(
        `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
          2,
          "0"
        )}`
      );
    };

    updateCountdown();

    const timer = setInterval(updateCountdown, 1000);

    return () => clearInterval(timer);
  }, [myActiveTrade]);

  const pakistanTimeState = useMemo(() => {
    const parts = getPakistanParts(pakistanNow);
    const totalMinutes = parts.hour * 60 + parts.minute;
    const firstStartMinutes = 13 * 60;
    const firstEndMinutes = 13 * 60 + 10;
    const secondStartMinutes = 20 * 60;
    const secondEndMinutes = 20 * 60 + 10;

    return {
      dateKey: parts.dateKey,
      isOpen:
        (totalMinutes >= firstStartMinutes && totalMinutes <= firstEndMinutes) ||
        (totalMinutes >= secondStartMinutes && totalMinutes <= secondEndMinutes),
    };
  }, [pakistanNow]);

  const historyRows = useMemo(() => {
    return history.map((item) => ({
      id: item.id,
      pair: item.pair || "BTC/USDT",
      investedAmount: item.investedAmount || 0,
      profitAmount: item.profitAmount || 0,
      returnAmount: item.returnAmount || 0,
      result: item.result || "win",
      completedAt: item.completedAt || item.joinedAt,
      status: item.status || "completed",
      joinedAt: item.joinedAt,
    }));
  }, [history]);

  const todayTradeUsed = useMemo(() => {
    let usedCount = 0;

    if (
      myActiveTrade?.joinedAt &&
      getPakistanParts(myActiveTrade.joinedAt).dateKey ===
        pakistanTimeState.dateKey
    ) {
      usedCount += 1;
    }

    historyRows.forEach((item) => {
      const sourceDate = item.joinedAt || item.completedAt;
      if (!sourceDate) return;
      if (getPakistanParts(sourceDate).dateKey === pakistanTimeState.dateKey) {
        usedCount += 1;
      }
    });

    return usedCount >= 2;
  }, [historyRows, myActiveTrade, pakistanTimeState.dateKey]);

  const isTradeWindowOpen = pakistanTimeState.isOpen;

  const handleJoinTrade = async () => {
    const tradeId = liveSlot?.id;
    const token = getToken();

    if (!tradeId) return;

    if (!token) {
      alert("Please login first.");
      navigate("/login");
      return;
    }

    if (!isTradeWindowOpen) {
      alert(
        "Trade is available only from 01:00 PM to 01:10 PM PKT and 08:00 PM to 08:10 PM PKT."
      );
      return;
    }

    if (todayTradeUsed) {
      alert("You have already used both trades today.");
      return;
    }

    try {
      setJoiningTrade(true);

      const response = await fetch(`${API_BASE_URL}/api/copy-trades/join`, {
        method: "POST",
        headers: getAuthHeaders(true),
        body: JSON.stringify({ tradeId }),
      });

      const data = await response.json();

      if (response.status === 401) {
        alert("Session expired or unauthorized. Please login again.");
        localStorage.removeItem("token");
        localStorage.removeItem("primeriseToken");
        localStorage.removeItem("naxoraToken");
        localStorage.removeItem("naxoraAuth");
        localStorage.removeItem("isAuthenticated");
        navigate("/login");
        return;
      }

      if (response.status === 403 && data.kycRequired) {
        const goToProfile = window.confirm(
          data.message ||
            "KYC verification is required before starting a copy trade. Go to Profile now?"
        );

        if (goToProfile) {
          navigate("/profile");
        }

        return;
      }

      if (!response.ok) {
        alert(data.message || "Copy trade start failed");
        return;
      }

      alert(data.message || "Copy trade started successfully");
      await refreshAll(true);
    } catch (error) {
      console.error("Join trade error:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setJoiningTrade(false);
    }
  };

  const runningTradeCard = useMemo(() => {
    if (!myActiveTrade) return null;

    return {
      title: myActiveTrade.title || "Copy Trade",
      pair: myActiveTrade.pair || "BTC/USDT",
      investedAmount: myActiveTrade.investedAmount || 0,
      profitAmount: myActiveTrade.profitAmount || 0,
      returnAmount: myActiveTrade.returnAmount || 0,
      joinedAt: myActiveTrade.joinedAt,
      settlementAt: myActiveTrade.settlementAt,
    };
  }, [myActiveTrade]);

  const joinButtonText = useMemo(() => {
    if (myActiveTrade) return "Trade Running...";
    if (todayTradeUsed) return "Today's Both Trades Used";
    if (!isTradeWindowOpen) return "Trade Time 01:00 PM / 08:00 PM";
    if (joiningTrade) return "Starting...";
    return "Start Copy Trade";
  }, [joiningTrade, isTradeWindowOpen, myActiveTrade, todayTradeUsed]);

  const signalTimeRows = useMemo(() => {
    return [
      { label: "America", value: "04:00 AM / 11:00 AM" },
      { label: "UTC", value: "08:00 AM / 03:00 PM" },
      { label: "Pakistan", value: "01:00 PM / 08:00 PM" },
      { label: "Saudi Arabia", value: "11:00 AM / 06:00 PM" },
      { label: "Dubai", value: "12:00 PM / 07:00 PM" },
      { label: "Bangladesh", value: "02:00 PM / 09:00 PM" },
      { label: "India", value: "01:30 PM / 08:30 PM" },
    ];
  }, []);

  return (
    <div className="copytrade-page">
      <div className="copytrade-wrapper">
        <div className="copytrade-header">
          <div>
            <h1>Copy Trade</h1>
            <p>
              Trade is available daily from 01:00 PM to 01:10 PM PKT and 08:00
              PM to 08:10 PM PKT
            </p>
          </div>
        </div>

        <div className="copytrade-main-grid">
          <div className="copytrade-active-card">
            <div className="card-head">
              <h3>Copy Trading</h3>
              <span
                className={`trade-status ${
                  myActiveTrade
                    ? "active"
                    : isTradeWindowOpen
                    ? "active"
                    : "inactive"
                }`}
              >
                {myActiveTrade
                  ? "Running"
                  : todayTradeUsed
                  ? "Used"
                  : isTradeWindowOpen
                  ? "Available"
                  : "Closed"}
              </span>
            </div>

            {loadingLiveSlot || loadingMyTrade ? (
              <div className="empty-state">Loading trade...</div>
            ) : myActiveTrade ? (
              <>
                <div className="live-trade-top">
                  <div className="live-trade-pair">
                    <span className="copytrade-label">Pair</span>
                    <h2>{runningTradeCard.pair}</h2>
                  </div>

                  <div className="live-trade-timer">
                    <span className="copytrade-label">Countdown</span>
                    <h2>{countdown}</h2>
                  </div>
                </div>

                <div className="live-trade-grid">
                  <div className="live-trade-item">
                    <span>Trade Title</span>
                    <strong>{runningTradeCard.title}</strong>
                  </div>

                  <div className="live-trade-item">
                    <span>Status</span>
                    <strong>Running</strong>
                  </div>
                </div>

                <div className="live-trade-note">Your trade is active.</div>

                <button type="button" className="join-trade-btn" disabled>
                  {joinButtonText}
                </button>
              </>
            ) : !liveSlot ? (
              <div className="empty-state">No active copy trade right now</div>
            ) : (
              <>
                <div className="live-trade-top">
                  <div className="live-trade-pair">
                    <span className="copytrade-label">Pair</span>
                    <h2>{liveSlot.pair || "BTC/USDT"}</h2>
                  </div>

                  <div className="live-trade-timer">
                    <span className="copytrade-label">Trade Time</span>
                    <h2>01:00 PM - 01:10 PM / 08:00 PM - 08:10 PM</h2>
                  </div>
                </div>

                <div className="live-trade-grid">
                  <div className="live-trade-item">
                    <span>Trade Title</span>
                    <strong>{liveSlot.title || "Copy Trade"}</strong>
                  </div>

                  <div className="live-trade-item">
                    <span>Status</span>
                    <strong>
                      {todayTradeUsed
                        ? "Used Today"
                        : isTradeWindowOpen
                        ? "Ready"
                        : "Closed"}
                    </strong>
                  </div>

                  <div className="live-trade-item">
                    <span>Result Time</span>
                    <strong>10 Minutes After Trade</strong>
                  </div>

                  <div className="live-trade-item">
                    <span>Timezone</span>
                    <strong>Pakistan Time</strong>
                  </div>
                </div>

                <div className="live-trade-note">
                  Two trades are allowed per day during the live trade windows.
                </div>

                <button
                  type="button"
                  className="join-trade-btn"
                  onClick={handleJoinTrade}
                  disabled={joiningTrade || todayTradeUsed || !isTradeWindowOpen}
                >
                  {joinButtonText}
                </button>
              </>
            )}
          </div>

          <div className="copytrade-side-card">
            <div className="card-head">
              <h3>Signal Schedule</h3>
            </div>

            <div className="rules-list">
              {signalTimeRows.map((item) => (
                <div className="rule-item" key={item.label}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="copytrade-history-card">
          <div className="card-head">
            <h3>Trade History</h3>
          </div>

          {loadingHistory ? (
            <div className="empty-state">Loading history...</div>
          ) : historyRows.length === 0 ? (
            <div className="empty-state">No trade history available</div>
          ) : (
            <div className="history-list">
              {historyRows.map((item) => {
                const displayDate = item.completedAt || item.joinedAt;
                return (
                  <div className="history-row" key={item.id}>
                    <div className="history-left">
                      <h4>{formatHistoryDayLabel(displayDate)}</h4>
                      <span>{formatPakistanDateTime(displayDate)}</span>
                    </div>

                    <div className="history-middle">
                      <div className="history-price-box">
                        <span>Profit</span>
                        <strong>{formatPrice(item.profitAmount)}</strong>
                      </div>
                    </div>

                    <div className="history-right">
                      <strong className="history-direction call">WIN</strong>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CopyTrade;